import { convertFromRaw, convertToRaw, EditorState, Modifier } from 'draft-js';
import { draftToMarkdown, markdownToDraft } from 'markdown-draft-js';
import React, { FunctionComponent, useCallback, useEffect, useRef, useState, type JSX } from 'react';
import { Editor } from 'react-draft-wysiwyg';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import FieldLabel from '../fieldLabel/FieldLabel';
import {
  InputColourVariant,
  InputStyleVariant,
} from '../inputWrapper/InputWrapper';
import { Emojis } from './editorConfig';
import './TextareaField.sass';

export enum ToolbarColourVariant {
  PRIMARY = 'primary',
  SECONDARY = 'secondary',
  WHITE = 'white',
}

type Props = {
  styleVariant?: InputStyleVariant;
  colourVariant?: InputColourVariant;
  toolbarColourVariant?: ToolbarColourVariant;
  description?: string | JSX.Element;
  isDisabled?: boolean;
  isRequired?: boolean;
  label?: string | JSX.Element;
  name: string;
  onChange?: (value: { [key: string]: string }) => void;
  onTouch?: (fieldName: string) => void;
  placeholder?: string;
  value?: any;
  changeTimeoutMs?: number;
  onBlur?: () => void;
  onChangeTimeout?: () => void;
  additionalEditorProps?: { [key: string]: any };
  hasBoxShadow?: boolean;
  bindEventHandlers?: (handlerObj: { insertText: any }) => void;
};

const TextareaFieldFormatter: FunctionComponent<
  React.PropsWithChildren<Props>
> = ({
  styleVariant = InputStyleVariant.STANDARD,
  colourVariant = InputColourVariant.WHITE,
  toolbarColourVariant = ToolbarColourVariant.WHITE,
  hasBoxShadow = true,
  description,
  isDisabled = false,
  isRequired = false,
  label,
  name,
  onChange,
  onTouch,
  placeholder,
  value,
  onBlur,
  onChangeTimeout,
  changeTimeoutMs = 1000,
  bindEventHandlers,
  additionalEditorProps,
}: React.PropsWithChildren<Props>): JSX.Element => {
  const [editorRef, setEditorRef] = useState<any>();
  const changeTimeoutRef = useRef<any>(undefined);
  const [editorState, setEditorState] = useState(EditorState.createEmpty());
  const hasInitialisedRef = useRef<boolean>(false);

  const onInputTouch = useCallback(
    (event: React.FocusEvent<HTMLTextAreaElement>) => {
      // oxlint-disable-next-line eslint(no-unused-expressions) -- FIXME: pre-existing error: Expected expression to be used
      onTouch && onTouch(name);
    },
    [onTouch, name]
  );

  useEffect(() => {
    // only initialise when a value exists
    if (value !== undefined && !hasInitialisedRef.current) {
      hasInitialisedRef.current = true;
      // convert md to editorstate
      setEditorState(
        EditorState.createWithContent(
          convertFromRaw(
            markdownToDraft(value, {
              preserveNewlines: true,
            })
          )
        )
      );
    }
  // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useEffect has an unnecessary dependency: hasInitialisedRef.
  }, [hasInitialisedRef.current, value]);

  const insertTextHandler = useCallback(
    (text: string) => {
      if (!editorRef) return;
      const currentContent = editorState.getCurrentContent();
      const currentSelection = editorState.getSelection();

      const newContent = Modifier.replaceText(
        currentContent,
        currentSelection,
        text
      );

      const newEditorState = EditorState.push(
        editorState,
        newContent,
        'insert-characters'
      );
      editorRef.focusEditor();
      onEditorChange(
        EditorState.forceSelection(
          newEditorState,
          newContent.getSelectionAfter()
        )
      );
    },
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useCallback has a missing dependency: 'onEditorChange'
    [editorRef, editorState]
  );

  useEffect(() => {
    // oxlint-disable-next-line eslint(no-unused-expressions) -- FIXME: pre-existing error: Expected expression to be used
    bindEventHandlers &&
      bindEventHandlers({
        insertText: insertTextHandler,
      });
  // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useEffect has a missing dependency: 'bindEventHandlers'
  }, [insertTextHandler]);

  const onEditorChange = useCallback(
    (editorStateChange: any) => {
      const content = editorStateChange.getCurrentContent();
      const rawObject = convertToRaw(content);
      const markdownString = draftToMarkdown(rawObject, {
        preserveNewlines: true,
      });
      // oxlint-disable-next-line eslint(no-unused-expressions) -- FIXME: pre-existing error: Expected expression to be used
      onChange &&
        onChange({
          [name]: markdownString,
        });
      setEditorState(editorStateChange);
      // oxlint-disable-next-line eslint(no-unused-expressions) -- FIXME: pre-existing error: Expected expression to be used
      changeTimeoutRef.current && clearTimeout(changeTimeoutRef.current);
      changeTimeoutRef.current = setTimeout(() => {
        // oxlint-disable-next-line eslint(no-unused-expressions) -- FIXME: pre-existing error: Expected expression to be used
        onChangeTimeout && onChangeTimeout();
      }, changeTimeoutMs);
    },
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useCallback has an unnecessary dependency: changeTimeoutRef.
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useCallback has a missing dependency: 'name'
    [onChange, onChangeTimeout, changeTimeoutRef.current, changeTimeoutMs]
  );

  return (
    <div className="text-area-field-formatter">
      <FieldLabel
        label={label}
        isRequired={isRequired}
        description={description}
      />
      <Editor
        ref={setEditorRef}
        editorClassName={`text-area-field-formatter-editor wrap-break-word`}
        toolbarClassName={`text-area-field-formatter-toolbar toolbar-col-${toolbarColourVariant}`}
        editorState={editorState}
        onEditorStateChange={onEditorChange}
        toolbar={{
          options: ['inline', 'emoji', 'list', 'link'],
          inline: {
            options: ['bold', 'italic', 'strikethrough'],
          },
          emoji: {
            emojis: Emojis,
          },
        }}
        placeholder={placeholder}
        onBlur={(event: React.FocusEvent<HTMLTextAreaElement>) => {
          // oxlint-disable-next-line eslint(no-unused-expressions) -- FIXME: pre-existing error: Expected expression to be used
          onInputTouch && onInputTouch(event);
          // oxlint-disable-next-line eslint(no-unused-expressions) -- FIXME: pre-existing error: Expected expression to be used
          onBlur && onBlur();
        }}
        handlePastedText={() => false}
        spellCheck={true}
        {...additionalEditorProps}
      />
    </div>
  );
};

export default TextareaFieldFormatter;
